/**
 * Dialoguri de confirmare / informare proprii, în locul `confirm()` și
 * `alert()` native: arată ca restul aplicației, au butoane traduse și nu
 * blochează firul principal. API imperativ, bazat pe Promise:
 *
 *   if (await confirmDialog({ message: t('...') })) { ... }
 *   await alertDialog(t('...'));
 *
 * `DialogHost` (montat o singură dată, în main.tsx) le randează.
 */
import { create } from 'zustand';

export interface DialogRequest {
  id: number;
  kind: 'confirm' | 'alert';
  message: string;
  title?: string;
  /** Eticheta butonului principal; implicit „Continuă" / „Închide". */
  confirmLabel?: string;
  /** Acțiune distructivă → buton roșu. */
  danger?: boolean;
  resolve: (ok: boolean) => void;
}

interface DialogState {
  queue: DialogRequest[];
  push: (request: DialogRequest) => void;
  settle: (id: number, ok: boolean) => void;
}

export const useDialogStore = create<DialogState>((set, get) => ({
  queue: [],
  push: (request) => set({ queue: [...get().queue, request] }),
  settle: (id, ok) => {
    const request = get().queue.find((r) => r.id === id);
    if (!request) return;
    set({ queue: get().queue.filter((r) => r.id !== id) });
    request.resolve(ok);
  },
}));

let nextId = 1;

type Options = Omit<DialogRequest, 'id' | 'kind' | 'resolve'>;

export function confirmDialog(options: Options | string): Promise<boolean> {
  const opts = typeof options === 'string' ? { message: options } : options;
  return new Promise((resolve) => {
    useDialogStore.getState().push({ ...opts, id: nextId++, kind: 'confirm', resolve });
  });
}

export function alertDialog(options: Options | string): Promise<void> {
  const opts = typeof options === 'string' ? { message: options } : options;
  return new Promise((resolve) => {
    useDialogStore
      .getState()
      .push({ ...opts, id: nextId++, kind: 'alert', resolve: () => resolve() });
  });
}
